// Submit handler for the wedding-fayre enquiry form on /wedding-fayre/.
// Tags the lead with ?fayre=<name>, turns off autofill in ?kiosk mode (a
// shared tablet on the stand), wires up Turnstile, validates basics, POSTs
// to the worker's /wedding-fayre endpoint, and toggles inline state.

import { onReady } from "#public/utils/on-ready.js";

const FORM_SELECTOR = "form[data-fayre-form]";
const SUBMIT_LABEL = "Claim my extra hour";

const TURNSTILE_WIDGETS = new Map();

const whenTurnstileReady = () =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(null);
    if (window.turnstile) return resolve(window.turnstile);
    const start = Date.now();
    const poll = () => {
      if (window.turnstile) return resolve(window.turnstile);
      if (Date.now() - start > 10000) return resolve(null);
      setTimeout(poll, 100);
    };
    poll();
  });

const renderTurnstile = async (placeholderId, sitekey) => {
  if (!placeholderId || !sitekey) return;
  const placeholder = document.getElementById(placeholderId);
  if (!placeholder) return;
  if (TURNSTILE_WIDGETS.has(placeholderId)) return;
  const turnstile = await whenTurnstileReady();
  if (!turnstile) return;
  try {
    const id = turnstile.render(`#${placeholderId}`, {
      sitekey,
      action: "wedding-fayre",
      theme: "light",
    });
    if (id) TURNSTILE_WIDGETS.set(placeholderId, id);
  } catch (_e) {
    // No-op: render can throw if already mounted.
  }
};

const resetTurnstile = (form) => {
  if (typeof window === "undefined" || !window.turnstile) return;
  const placeholder = form.querySelector("[data-fayre-turnstile]");
  const widgetId = placeholder && TURNSTILE_WIDGETS.get(placeholder.id);
  if (!widgetId) return;
  try {
    window.turnstile.reset(widgetId);
  } catch (_e) {
    // No-op
  }
};

const getValue = (form, name) => {
  const el = form.querySelector(`[name="${name}"]`);
  return el && typeof el.value === "string" ? el.value.trim() : "";
};

const getCheckedValues = (form, name) =>
  Array.from(
    form.querySelectorAll(`input[name="${name}"]:checked`),
    (el) => el.value,
  );

const getTurnstileToken = (form) => {
  const el = form.querySelector("[name='cf-turnstile-response']");
  return el && el.value ? el.value : "";
};

const showMessage = (form, kind, text) => {
  const msg = form.querySelector("[data-fayre-msg]");
  if (!msg) return;
  msg.hidden = false;
  msg.classList.remove("lb-form__msg--success", "lb-form__msg--error");
  msg.classList.add(`lb-form__msg--${kind}`);
  msg.textContent = text;
};

const clearMessage = (form) => {
  const msg = form.querySelector("[data-fayre-msg]");
  if (!msg) return;
  msg.hidden = true;
  msg.textContent = "";
};

const setSubmitting = (form, isSubmitting) => {
  const btn = form.querySelector("[data-fayre-submit]");
  if (!btn) return;
  btn.disabled = isSubmitting;
  btn.textContent = isSubmitting ? "Sending…" : SUBMIT_LABEL;
};

const buildPayload = (form) => ({
  turnstile_token: getTurnstileToken(form),
  website: getValue(form, "website"),
  name: getValue(form, "name"),
  email: getValue(form, "email"),
  mobile: getValue(form, "mobile"),
  wedding_date: getValue(form, "wedding_date"),
  venue: getValue(form, "venue"),
  interests: getCheckedValues(form, "interests"),
  fayre: getValue(form, "fayre"),
  message: getValue(form, "message"),
});

const validate = (payload) => {
  if (!payload.name) return "Please add your name.";
  if (!payload.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payload.email)) {
    return "Please add a valid email address.";
  }
  return null;
};

const postLead = async (workerUrl, payload) => {
  try {
    const response = await fetch(
      `${workerUrl.replace(/\/$/, "")}/wedding-fayre`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      },
    );
    return response.ok;
  } catch (_err) {
    return false;
  }
};

const preSubmitError = (form, payload) => {
  if (!(form.dataset.workerUrl || "").trim()) {
    return "Form isn't configured — please call us on 03331 126993.";
  }
  const validationError = validate(payload);
  if (validationError) return validationError;
  if (!payload.turnstile_token) {
    return "Please wait for the security check to finish, then submit again.";
  }
  return null;
};

const submitForm = async (form) => {
  clearMessage(form);

  const payload = buildPayload(form);
  const error = preSubmitError(form, payload);
  if (error) {
    showMessage(form, "error", error);
    return;
  }

  setSubmitting(form, true);
  const ok = await postLead(form.dataset.workerUrl.trim(), payload);
  setSubmitting(form, false);
  resetTurnstile(form);

  if (!ok) {
    showMessage(
      form,
      "error",
      "Sorry, something went wrong sending your details. Please try again or call us on 03331 126993.",
    );
    return;
  }

  // reset() clears the hidden fayre field's value back to its HTML default,
  // which tagFayre set — so the next couple on a shared tablet keeps the tag.
  form.reset();
  showMessage(
    form,
    "success",
    "Thank you! Your extra hour is noted. Check your inbox for a confirmation, and we'll be in touch soon.",
  );
  form.querySelector("[data-fayre-msg]")?.scrollIntoView({
    behavior: "smooth",
    block: "center",
  });
};

// Write ?fayre= into the hidden field's default value (not just .value) so
// form.reset() after each submission keeps it.
const tagFayre = (form, params) => {
  const fayre = (params.get("fayre") || "").trim().slice(0, 100);
  const field = form.querySelector("[name='fayre']");
  if (field && fayre) field.defaultValue = fayre;
};

const applyKioskMode = (form, params) => {
  if (!params.has("kiosk")) return;
  form.setAttribute("autocomplete", "off");
  for (const el of form.querySelectorAll("input, textarea")) {
    el.setAttribute("autocomplete", "off");
  }
};

onReady(() => {
  const params = new URLSearchParams(window.location.search);
  for (const form of document.querySelectorAll(FORM_SELECTOR)) {
    tagFayre(form, params);
    applyKioskMode(form, params);

    const placeholder = form.querySelector("[data-fayre-turnstile]");
    if (placeholder && placeholder.id) {
      renderTurnstile(placeholder.id, form.dataset.turnstileKey || "");
    }

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      submitForm(form);
    });
  }
});
